"use client";

import { TriggerAuthContext } from "@trigger.dev/react-hooks";
import { createContext, type ReactNode, useContext, useState } from "react";

interface TriggerContextValue {
	setAccessToken: (token: string | null) => void;
	accessToken: string | null;
}

const TriggerContext = createContext<TriggerContextValue | null>(null);

export function TriggerProvider({ children }: { children: ReactNode }) {
	const [accessToken, setAccessTokenState] = useState<string | null>(null);

	// Don't persist tokens in sessionStorage to avoid cross-job contamination
	// Each job should set its own token when the page loads

	// Custom setter that only updates state (no persistence)
	const setAccessToken = (token: string | null) => {
		// Only accept valid tokens
		const validToken = token && token.trim() !== "" ? token : null;
		setAccessTokenState(validToken);
	};

	return (
		<TriggerContext.Provider value={{ accessToken, setAccessToken }}>
			<TriggerAuthContext.Provider
				value={{
					// Provide accessToken as documented in official Trigger.dev docs
					// Always include the property, using undefined when not available
					accessToken:
						accessToken && accessToken.trim() !== "" ? accessToken : undefined,
				}}
			>
				{children}
			</TriggerAuthContext.Provider>
		</TriggerContext.Provider>
	);
}

export function useTriggerAuth() {
	const context = useContext(TriggerContext);
	if (!context) {
		throw new Error("useTriggerAuth must be used within a TriggerProvider");
	}
	return context;
}
